import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import { normalizePeriodName } from '../../../../utils/scheduleTargetUtils';
import { getPresetDateRangeParams } from '../../../../utils/measurementUtils';
import { PATIENT_SCHEDULE_TARGET_PREF_EVENT } from '../../../../utils/patientScheduleTargetPreference';
import './MeanChart.css';

const MeanChart = ({ practiceId, patientId }) => {
  const navigate = useNavigate();
  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      fetchPatientData();
      fetchScheduleTargets();
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  // Fetch measurements when query parameters change
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId, dateRange, fromDate, toDate]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (fromDateParam && toDateParam) {
        const response = await measurementService.getBloodPressureByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          'date'
        );
        if (response.data?.success) {
          setMeasurements(response.data.data || []);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load measurements');
      }
      setMeasurements([]);
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQuery = () => {
    fetchMeasurements();
  };

  const patientName = patientData?.patient
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (!scheduleTargets?.bloodPressureTimePeriod) {
      return ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];
    }
    return scheduleTargets.bloodPressureTimePeriod.map(p => p.name);
  }, [scheduleTargets]);

  // Calculate mean values by period
  const periodMeans = useMemo(() => {
    if (!measurements || measurements.length === 0) {
      return periodNames.map(name => ({ name, sys: null, dia: null }));
    }

    return periodNames.map(periodName => {
      const periodMeasurements = measurements.filter(m => {
        const raw = m.period_name || m.period || m.measure_note || m.note || 'Unknown';
        return normalizePeriodName(raw) === normalizePeriodName(periodName);
      });

      if (periodMeasurements.length === 0) {
        return { name: periodName, sys: null, dia: null };
      }

      const sysValues = periodMeasurements
        .map(m => parseFloat(m.systolic_pressure))
        .filter(v => !isNaN(v));
      const diaValues = periodMeasurements
        .map(m => parseFloat(m.diastolic_pressure))
        .filter(v => !isNaN(v));

      const sysMean = sysValues.length > 0
        ? sysValues.reduce((a, b) => a + b, 0) / sysValues.length
        : null;
      const diaMean = diaValues.length > 0
        ? diaValues.reduce((a, b) => a + b, 0) / diaValues.length
        : null;

      return { name: periodName, sys: sysMean, dia: diaMean };
    });
  }, [measurements, periodNames]);

  // Chart dimensions and scaling — increased size
  const chartWidth = 1100;
  const chartHeight = 400;
  const padding = { top: 20, right: 30, bottom: 60, left: 80 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 200;
  const minY = 0;

  // Scale functions
  const scaleX = (index) => {
    if (periodNames.length <= 1) return padding.left + plotWidth / 2;
    return padding.left + (index / (periodNames.length - 1)) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  // Bar width based on available space
  const barWidth = Math.min(40, plotWidth / (periodNames.length * 3));

  return (
    <div className="mean-chart-page">
      {/* Header Section — hidden when embedded */}
      <div className="mc-header">
        <button className="mc-nav-button active">Blood Pressure Chart</button>
        <div className="mc-patient-info">
          <div className="mc-info-row">
            <span className="mc-label">Name :</span>
            <span className="mc-value">{patientName}</span>
          </div>
          <div className="mc-info-row">
            <span className="mc-label">DOB :</span>
            <span className="mc-value">{patientDOB}</span>
          </div>
        </div>
      </div>

      {/* Query Period Section — hidden when embedded */}
      <div className="mc-query-section">
        <h4>Query Period</h4>
        <div className="mc-radio-group">
          <label className="mc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="dateRange"
              checked={queryType === 'dateRange'}
              onChange={(e) => setQueryType(e.target.value)}
            />
            <span>Date Range</span>
            {queryType === 'dateRange' && (
              <select
                className="mc-dropdown"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option>Last 30 days</option>
                <option>Last 7 days</option>
                <option>Last 14 days</option>
                <option>Last 60 days</option>
                <option>Last 90 days</option>
              </select>
            )}
          </label>
          <label className="mc-radio-label">
            <input
              type="radio"
              name="queryType"
              value="from"
              checked={queryType === 'from'}
              onChange={(e) => setQueryType(e.target.value)}
            />
            <span>From</span>
            {queryType === 'from' && (
              <div className="mc-date-inputs">
                <div className="mc-date-wrapper">
                  <input
                    type="date"
                    className="mc-date-input"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  />
                </div>
                <span className="mc-date-separator">to</span>
                <div className="mc-date-wrapper">
                  <input
                    type="date"
                    className="mc-date-input"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  />
                </div>
              </div>
            )}
          </label>
        </div>
        <div className="mc-action-buttons">
          <button className="mc-action-btn" onClick={handleQuery} disabled={queryLoading}>
            {queryLoading ? 'Loading...' : 'Query'}
          </button>
          <button
            className="mc-action-btn"
            onClick={() => navigate(`/practices/${practiceId}/patients/${patientId}/measurements/blood-pressure/chart`)}
          >
            Daily Trend Chart
          </button>
          <button className="mc-action-btn">Print</button>
          <button className="mc-action-btn">Export</button>
          <button className="mc-action-btn">Email</button>
        </div>
      </div>

      {/* Mean Chart Section — wrapped in fieldset/legend */}
      <fieldset className="mc-fieldset">
        <legend className="mc-legend">Measurement Period Comparison Chart</legend>
        <div className="mc-chart-title-center">Measurement Period Comparison Chart of Blood Pressure</div>
        <div className="mc-chart-container">
          <div className="mc-chart-legend">
            <div className="mc-legend-item">
              <div className="mc-legend-dot mc-legend-systolic"></div>
              <span>Systolic Pressure</span>
            </div>
            <div className="mc-legend-item">
              <div className="mc-legend-dot mc-legend-diastolic"></div>
              <span>Diastolic Pressure</span>
            </div>
          </div>
          <div className="mc-chart-area" style={{ border: 'none', padding: '20px 0', overflowX: 'auto' }}>
            {loading || queryLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading chart data...</div>
            ) : periodMeans.every(p => p.sys == null && p.dia == null) ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
            ) : (
              <div style={{ display: 'block', minWidth: `${chartWidth}px`, margin: '0 auto', position: 'relative' }}>
                <svg width={chartWidth} height={chartHeight} style={{ display: 'block' }}>
                  {/* Y-axis Label — with extra spacing from axis */}
                  <text
                    x={18}
                    y={padding.top + plotHeight / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#333"
                    fontSize="14"
                    fontWeight="600"
                    transform={`rotate(-90, 18, ${padding.top + plotHeight / 2})`}
                  >
                    Blood Pressure mmHg
                  </text>

                  {/* Grid border */}
                  <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} fill="none" stroke="#e0e0e0" strokeWidth="1" />

                  {/* Horizontal Grid lines and Y-axis text */}
                  {[0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200].map(value => {
                    const y = scaleY(value);
                    if (y == null) return null;
                    return (
                      <g key={`y-${value}`}>
                        <line
                          x1={padding.left}
                          y1={y}
                          x2={chartWidth - padding.right}
                          y2={y}
                          stroke="#ECEDEE"
                          strokeWidth="1"
                        />
                        <text
                          x={padding.left - 12}
                          y={y}
                          textAnchor="end"
                          dominantBaseline="middle"
                          fill="#666"
                          fontSize="12"
                        >
                          {value}
                        </text>
                      </g>
                    );
                  })}

                  {/* Vertical grid lines for each period */}
                  {periodNames.map((periodName, index) => {
                    const x = scaleX(index);
                    return (
                      <g key={`vl-${periodName}`}>
                        <line
                          x1={x}
                          y1={padding.top}
                          x2={x}
                          y2={chartHeight - padding.bottom}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                        />
                        {/* X-axis Text — period names */}
                        <text
                          x={x}
                          y={chartHeight - padding.bottom + 20}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#333"
                          fontSize="13"
                          fontWeight="500"
                        >
                          {periodName}
                        </text>
                      </g>
                    );
                  })}

                  {/* Systolic bars (red) */}
                  {periodMeans.map((period, index) => {
                    if (period.sys == null) return null;
                    const x = scaleX(index);
                    const y = scaleY(period.sys);
                    const barH = plotHeight + padding.top - y;
                    if (y == null) return null;

                    return (
                      <g key={`sys-${period.name}`}>
                        <rect
                          x={x - barWidth - 2}
                          y={y}
                          width={barWidth}
                          height={barH}
                          fill="#e53e3e"
                          rx="2"
                        />
                        <text
                          x={x - barWidth / 2 - 2}
                          y={barH > 25 ? y + 18 : y - 8}
                          fill={barH > 25 ? "#ffffff" : "#e53e3e"}
                          textAnchor="middle"
                          fontSize="12"
                          fontWeight="bold"
                        >
                          {Math.round(period.sys)}
                        </text>
                      </g>
                    );
                  })}

                  {/* Diastolic bars (blue) */}
                  {periodMeans.map((period, index) => {
                    if (period.dia == null) return null;
                    const x = scaleX(index);
                    const y = scaleY(period.dia);
                    const barH = plotHeight + padding.top - y;
                    if (y == null) return null;

                    return (
                      <g key={`dia-${period.name}`}>
                        <rect
                          x={x + 2}
                          y={y}
                          width={barWidth}
                          height={barH}
                          fill="#3182ce"
                          rx="2"
                        />
                        <text
                          x={x + barWidth / 2 + 2}
                          y={barH > 25 ? y + 18 : y - 8}
                          fill={barH > 25 ? "#ffffff" : "#3182ce"}
                          textAnchor="middle"
                          fontSize="12"
                          fontWeight="bold"
                        >
                          {Math.round(period.dia)}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '13px', fontWeight: '500' }}>
                  Time of Day (6 AM - 10 PM)
                </div>
              </div>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default MeanChart;
